import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Card,
  CardContent,
  CardMedia,
  useTheme,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import BookIcon from '@mui/icons-material/Book';
import { getImagePath } from '../utils/imageUtils';

const features = [
  {
    icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
    title: 'Olympiad Excellence',
    description: 'Specialized training for various Olympiads including IMO, ISO, IEO, ISSO and IHO.',
  },
  {
    icon: <SchoolIcon sx={{ fontSize: 40 }} />,
    title: 'Expert Olympiad Trainers',
    description: 'Learn from experienced Olympiad trainers who have mentored winners.',
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 40 }} />,
    title: 'Small Batch Size',
    description: 'Personal attention with limited students per batch for better learning.',
  },
  {
    icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
    title: 'Flexible Timing',
    description: 'Choose from multiple time slots that suit your schedule.',
  },
  {
    icon: <LightbulbIcon sx={{ fontSize: 40 }} />,
    title: 'Problem-Solving Focus',
    description: 'Emphasis on developing analytical and problem-solving skills.',
  },
  {
    icon: <BookIcon sx={{ fontSize: 40 }} />,
    title: 'Comprehensive Study Material',
    description: 'Comprehensive, expertly curated study material that builds strong concepts and problem-solving skills.',
  }
];

const Home = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 8,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h1" gutterBottom>
                Excel in Olympiads
              </Typography>
              <Typography variant="h5" paragraph>
                Join our Regular Foundation Courses and Specialized Olympiad Preparation Classes for Mathematics, Science, English, Social Studies and Hindi.
                Expert guidance for IMO, ISO, IEO, ISSO and IHO competitions.
              </Typography>
              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                  component={RouterLink}
                  to="/demo-class"
                  variant="contained"
                  color="secondary"
                  size="large"
                >
                  Book Free Demo Class
                </Button>
                <Button
                  component={RouterLink}
                  to="/courses"
                  variant="outlined"
                  color="inherit"
                  size="large"
                >
                  Explore Olympiad Courses
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={getImagePath('hero-image.jpg')}
                alt="Students preparing for Olympiads"
                sx={{
                  width: '100%',
                  maxWidth: 500,
                  borderRadius: 2,
                  boxShadow: 3,
                  display: { xs: 'none', md: 'block' },
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          Why Choose Our Regular and Olympiad Classes?
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" align="center" paragraph>
          Comprehensive preparation for various Olympiad competitions
        </Typography>

        <Grid container spacing={4} sx={{ mt: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="md">
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h4" component="h2" gutterBottom>
                Ready to Start Your Olympiad Journey?
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" paragraph>
                Join thousands of successful students who have won medals in various Olympiads.
              </Typography>
              <Button
                component={RouterLink}
                to="/demo-class"
                variant="contained"
                color="primary"
                size="large"
                sx={{ mt: 2 }}
              >
                Book Your Free Demo Class Today
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 